import { REB_TRANS_ALLOWED, REB_TRANS_HEAD_NOTES } from "./constants";

/** A column head on two tight lines, so a long head is no wider than its values (sized to the head it sits in). */
export const stackedHead = (top: string, bottom: string, title?: string) => (
  <span
    className="stk"
    title={title}
    style={{ display: "inline-block", fontSize: "0.85em", lineHeight: 1, verticalAlign: "bottom" }}
  >
    {top}
    <br />
    {bottom}
  </span>
);

/**
 * The "Shot Qual" column head with the scale it's on: shot quality is expected TS% in the overall and shot
 * distribution tables and expected 3P% in the three-point tables (Trevor, 2026-10-10).
 */
export const shotQualLabel = (scale: "TS%" | "3P%") =>
  stackedHead("Shot Qual", `(${scale})`, `Shot Quality: expected ${scale} from shot quality`);

/** The first-shot quadrants' overall-row heads, stacked so they are no wider than their values (IV1, 2026-10-10). */
export const FIRST_SHOT_LABELS = {
  "First Shot PPP": stackedHead("First Shot", "PPP", "First Shot PPP: points per halfcourt possession before its first offensive rebound"),
  "First Shot xPPP": stackedHead(
    "First Shot",
    "xPPP",
    "First Shot xPPP: those shots' and free throws' expected points, on the player-aware make probability",
  ),
};

/** Display heads for a table's "Shot Qual" column on the TS% scale (blobs before TR3 call it "xTS%"), and the
 * first-shot heads, which share its overall rows. */
export const SHOT_QUAL_TS_LABELS = {
  "Shot Qual": shotQualLabel("TS%"),
  "xTS%": shotQualLabel("TS%"),
  ...FIRST_SHOT_LABELS,
};

/** A rebounding / transition page's heads: the opponent's columns read "<metric>" over "allowed", the team's own
 * keep the plain name (constants REB_TRANS_ALLOWED). Works for any team's report, season or game. */
export const rebTransSideLabels = (side: "orb" | "drb") =>
  Object.fromEntries(
    Object.entries(REB_TRANS_ALLOWED[side])
      .filter(([metric, allowed]) => allowed || REB_TRANS_HEAD_NOTES[metric])
      .map(([metric, allowed]) => {
        const name = allowed ? `${metric} allowed` : metric;
        const title = REB_TRANS_HEAD_NOTES[metric] ? `${name}: ${REB_TRANS_HEAD_NOTES[metric]}` : name;
        return [metric, allowed ? stackedHead(metric, "allowed", title) : <span title={title}>{metric}</span>];
      }),
  );
